import { call, put, select, StrictEffect } from "redux-saga/effects";

import { Entity, Id } from "@/jenesys-types/data/common";
import { DataType } from "@/jenesys-types/data/enums";

import {
  authedFetch,
  create,
  del,
  list,
  Params,
  PathParams,
  performDataAction,
  retrieve,
  update
} from "@/services/api";
import {
  selectActiveClientId,
  selectActiveCompanyId
} from "@/store/features/companies/companies";
import { getBaseApiUrl } from "@/util/environment";

import {
  dataCreated,
  dataDeleted,
  dataLoaded,
  dataUpdated,
  resetExcluding,
  singletonLoaded,
  singletonUpdated
} from "./data";

/**
 * Custom function to perform a list data action with proper search parameters
 * when multiple search criteria are present
 */
export function* listDataWithSearchParams(
  dataType: DataType,
  queryParams: Record<string, any> = {},
  actionName: string = "list",
  pathParams: PathParams = {}
): Generator<StrictEffect, any, any> {
  const companyId = yield select(selectActiveCompanyId);
  const clientId = yield select(selectActiveClientId);

  const mergedPathParams = { companyId: clientId || companyId, ...pathParams };

  // Check if search is an array (multiple search criteria)
  const searchParams = queryParams.search;
  if (Array.isArray(searchParams)) {
    // Remove search from query params
    const { search: _searchParam, ...otherParams } = queryParams;

    // Determine the correct endpoint based on data type
    let endpoint;
    if (dataType === DataType.BankStatement) {
      // For BankStatement, use the transactions endpoint
      endpoint = `/companies/${mergedPathParams.companyId}/bank-accounts/${(mergedPathParams as any)?.accountId}/transactions`;
    } else {
      // For other data types, use the documents endpoint (existing behavior)
      endpoint = `/companies/${mergedPathParams.companyId}/documents`;
    }

    // Create a URL with all parameters except search
    const baseUrl = `${getBaseApiUrl()}${endpoint}`;

    // Build query string with remaining params
    const queryString = Object.entries(otherParams)
      .filter(([_key, value]) => value !== undefined && value !== null)
      .flatMap(([key, value]) => {
        // Ensure value is converted to string before encoding
        if (Array.isArray(value)) {
          // Do not send arrays as JSON
          return value.map((v) => `${key}=${encodeURIComponent(String(v))}`);
        }
        const stringValue =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        return `${key}=${encodeURIComponent(stringValue)}`;
      })
      .join("&");

    // Add each search parameter individually
    const searchQueryString = searchParams
      .map((param) => `search=${encodeURIComponent(param)}`)
      .join("&");

    // Combine all query parts
    const fullQueryString = queryString
      ? `${queryString}&${searchQueryString}`
      : searchQueryString;

    // Make direct request with properly formatted search parameters
    const fullUrl = `${baseUrl}?${fullQueryString}`;
    const response = yield call([authedFetch, authedFetch.get], fullUrl);
    return response.data;
  }

  // For single search parameter or no search, use standard method
  return yield call(
    performDataAction,
    dataType,
    actionName,
    mergedPathParams,
    queryParams
  );
}

/**
 * This saga exports wrappers of the API methods that also dispatch data
 * actions to keep the store state in sync. They return the raw response
 * from the API so that consuming sagas can use it however they require.
 *
 * They do not handle errors, that responsibility lies with the consumer.
 */
export function* retrieveData(
  dataType: DataType,
  id: string,
  pathParams: PathParams = {}
): Generator<StrictEffect, void, any> {
  const companyId = yield select(selectActiveCompanyId);
  const clientId = yield select(selectActiveClientId);

  const response = yield call(retrieve, dataType, {
    companyId: clientId || companyId,
    id,
    ...pathParams
  });
  yield put(dataLoaded(dataType, [response], pathParams));

  return response;
}

export function* retrieveSingletonData(
  dataType: DataType
): Generator<StrictEffect, Entity, any> {
  const companyId = yield select(selectActiveCompanyId);
  const response = yield call(retrieve, dataType, {
    companyId
  });

  yield put(singletonLoaded(dataType, response));

  return response;
}

/**
 * Updated listData function that uses our custom search implementation
 */
export function* listData(
  dataType: DataType,
  queryParams: Params = {},
  actionName: string = "list",
  pathParams: PathParams = {}
): Generator<StrictEffect, void, any> {
  const response = yield call(
    listDataWithSearchParams,
    dataType,
    queryParams,
    actionName,
    pathParams
  );

  yield put(dataLoaded(dataType, response.items, pathParams));

  return response;
}

export function* deleteData(
  dataType: DataType,
  id: string,
  pathParams: PathParams = {}
): Generator<StrictEffect, void, any> {
  const companyId = yield select(selectActiveCompanyId);
  const clientId = yield select(selectActiveClientId);
  const response = yield call(del, dataType, id, {
    companyId: clientId || companyId,
    ...pathParams
  });

  yield put(dataDeleted(dataType, [id], pathParams));

  return response;
}

export function* createData(
  dataType: DataType,
  data: any,
  pathParams: PathParams = {}
): Generator<StrictEffect, void, any> {
  const companyId = yield select(selectActiveCompanyId);
  const clientId = yield select(selectActiveClientId);
  const response = yield call(create, dataType, data, {
    companyId: clientId || companyId,
    ...pathParams
  });

  yield put(dataCreated(dataType, [response], pathParams));

  return response;
}

export function* updateData(
  dataType: DataType,
  id: string,
  data: any,
  pathParams: PathParams = {}
): Generator<StrictEffect, void, any> {
  const companyId = yield select(selectActiveCompanyId);
  const clientId = yield select(selectActiveClientId);
  const response = yield call(update, dataType, data, {
    companyId: clientId || companyId,
    id,
    ...pathParams
  });

  yield put(dataUpdated(dataType, [response], pathParams));

  return response;
}

export function* updateSingletonData(
  dataType: DataType,
  data: any
): Generator<StrictEffect, Entity, any> {
  const companyId = yield select(selectActiveCompanyId);
  const clientId = yield select(selectActiveClientId);
  const response = yield call(update, dataType, data, {
    companyId: clientId || companyId
  });

  yield put(singletonUpdated(dataType, response));

  return response;
}

/**
 * Performs an action on a single entity and pushes the response into
 * the data slice. Useful for actions where the action returns the updated entity.
 */
export function* performItemAction(
  dataType: DataType,
  id: Id,
  actionName: string,
  data?: any,
  queryParams: Params = {},
  pathParams: PathParams = {}
): Generator<StrictEffect, Entity, any> {
  const companyId = yield select(selectActiveCompanyId);
  const response = yield call(
    performDataAction,
    dataType,
    actionName,
    {
      companyId,
      [`${dataType}Id`]: id,
      ...pathParams
    },
    queryParams,
    data
  );

  yield put(dataUpdated(dataType, [response]));
  return response;
}

/**
 * Performs an action on a singleton and pushes the response into
 * the data slice. Useful for actions such as `/user/acceptTerms`
 * where the action returns the updated entity.
 */
export function* performSingletonAction(
  dataType: DataType,
  actionName: string,
  data?: any,
  queryParams: Params = {},
  pathParams: PathParams = {}
): Generator<StrictEffect, Entity, any> {
  const companyId = yield select(selectActiveCompanyId);
  const response = yield call(
    performDataAction,
    dataType,
    actionName,
    { companyId, ...pathParams },
    queryParams,
    data
  );

  yield put(singletonUpdated(dataType, response));
  return response;
}

// Helper for checking if at least one of a data type exists.
export function* hasAny(
  dataType: DataType,
  params: Params = {}
): Generator<StrictEffect, boolean, any> {
  const companyId = yield select(selectActiveCompanyId);
  const response = yield call(
    list,
    dataType,
    { ...params, limit: 1 },
    {
      companyId
    }
  );

  return response.data.length > 0;
}

export function* resetExcludingSaga(excludedDataTypes: Array<DataType> = []) {
  yield put(resetExcluding(excludedDataTypes));
}
